import {
  Backdrop,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid2,
  Icon,
  Switch,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import HandoverCallDetailsPopup from "../../../components/common/pop-up/HandoverCallDetailsPopup";
import SendEmailerConfirmationPopup from "../../../components/common/pop-up/SendEmailerConfirmationPopup";
import downloadIcon from "../../../../assets/download-icon.png";
import sendNPSIcon from "../../../../assets/send-nps-icon.png";
import orderProgressIcon from "../../../../assets/order-progress-icon.png";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { OrderType } from "../../../../models/Project/OrderType";
import { ShipmentModes } from "../../../../models/ProjectManagement/ShipmentModes";
import { ShippingType } from "../../../../models/ProjectManagement/ShippingType";
import { PaymentMode } from "../../../../models/ProjectManagement/PaymentMode";
import { ProjectPaymentStatus } from "../../../../models/ProjectManagement/ProjectPaymentStatus";
import { Admin } from "../../../../models/TaskTracker/Admin";
import { useStores } from "../../../../use-stores";
import AppDropDown from "../../../components/common/filter/AppDropDown";
import { observer } from "mobx-react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../../../utils/NotificationsContext";
import dayjs, { Dayjs } from "dayjs";
import DatePicker2 from "../../../components/common/textFields/DatePicker2";
import AppButton from "../../../components/common/buttons/AppButton";
import CustomTypography from "../../../components/common/form/CustomTypography";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import SendNpsPopup from "../../../components/projects/projectDetails/SendNpsPopup";
import { orderStatusOptions } from "../../../../models/Order/OrderDetails";
import RichTextEditor from "../../../components/common/textEditor/RichTextEditor";
import AppSingleSearchDropdown from "../../../components/common/filter/AppSingleSearchDropdown";
import PermissionBlockLabel from "../../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import CustomerDetailsCard from "../../../components/projects/projectDetails/CustomerDetailsCard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationPopup from "../../../components/common/pop-up/ConfirmationPopup";
import {
  project_emailers,
  ProjectEmailer,
  ProjectEmailerType,
  ProjectHandoverData,
} from "../../../../models/ProjectManagement/ProjectProductsSourcingVariants";
import AppButtonDropdown from "../../../components/common/buttons/AppButtonDropdown";
import { formatDate } from "../../../components/common/functions/formatDate";

const ProjectDetails = () => {
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const projectId = Number(params["project-id"]);
  const {
    orderStore,
    projectManagementStore,
    TaskTrackerStore,
    invoiceStore,
    userStore,
    warehouseStore,
  } = useStores();
  const { showError, showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendNpsPopup, setSendNpsPopup] = useState<boolean>(false);
  const [excludeFromProjectPopup, setExcludeFromProjectPopup] =
    useState<boolean>(false);
  const [excludeFromSourcingPopup, setExcludeFromSourcingPopup] =
    useState<boolean>(false);

  useEffect(() => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore.searchWarehouse().catch((err) => {
        console.error(err);
      });
    }

    if (projectManagementStore.currentProjectDetails?.order_id !== projectId) {
      setIsLoading(true);
      orderStore.getOrderBasicDetails(projectId).catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed to fetch comment, please try again later",
        );
      });
      projectManagementStore
        .getProjectDetails(projectId)
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch comment, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });

      projectManagementStore.fetchEmailersInfo(projectId).catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        console.log(
          errorMessage ||
            "Failed to fetch emailers info, please try again later"
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let apiTimer: any = 0;

  const handleAdminSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
          showError("Error", "Error occured while fetching admin");
        });
      }, 1000);
  };

  const handleSave = () => {
    const dispatchDate = projectManagementStore.currentProjectDetails
      ?.dispatch_date
      ? dayjs(projectManagementStore.currentProjectDetails.dispatch_date)
      : null;
    const deliveryDate = projectManagementStore.currentProjectDetails
      ?.delivery_date
      ? dayjs(projectManagementStore.currentProjectDetails.delivery_date)
      : null;

    if (dispatchDate && deliveryDate && dispatchDate.isAfter(deliveryDate)) {
      showError(
        "Invalid Date Range",
        "Dispatch date cannot be greater than delivery date. Please correct the dates before saving.",
      );
      return;
    }

    setIsLoading(true);
    projectManagementStore
      .saveProjectDetails()
      .catch((err) => {
        projectManagementStore.resetProjectDetails();
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendNps = (name: string, email: string) => {
    setIsLoading(true);
    projectManagementStore
      .sendNpsEmail(name, email, projectId)
      .then((res) => {
        if (res.success) {
          showSuccess("Email sent successfully");
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendCsat = () => {
    setIsLoading(true);
    projectManagementStore
      .sendCsatEmail(projectId)
      .then((res) => {
        if (res.success) {
          showSuccess("Message sent successfully");
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleStatusUpdate = (status: string, projectId: number) => {
    setIsLoading(true);
    orderStore
      .updateStatus(Number(status), projectId)
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadInvoice = () => {
    setIsLoading(true);
    invoiceStore
      .downloadInvoiceByOrderId(Number(projectId))
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");

          let filename = response.filename
            ? response.filename
            : `Invoice_${projectId}.zip`;

          if (!filename.endsWith(".zip")) {
            filename = filename.replace(/\.zip_?$/, "") + ".zip";
          }

          a.href = downloadUrl;
          a.download = filename;

          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download P&L.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const [handoverPopupOpen, setHandoverPopupOpen] = useState(false);
  const [showEmailersInfo, setShowEmailersInfo] = useState(false);
  const [emailerPopupOpen, setEmailerPopupOpen] = useState(false);

  const handleSendHandoverEmail = () => {
    projectManagementStore
      .fetchHandoverCallInfo(projectId)
      .then(() => {
        if (
          projectManagementStore.handoverCallInfo &&
          projectManagementStore.handoverCallInfo.products.length > 0
        ) {
          setHandoverPopupOpen(true);
        } else {
          showError(
            "Missing Variants Details",
            "No products found with variants and printing details. Add these details in the sourcing section.",
          );
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch handover call details, please try again later",
        );
      });
  };

  const fetchEmailerInfo = (emailer: ProjectEmailer) => {
    projectManagementStore
      .fetchEmailerInfo(projectId, emailer.type)
      .then(() => {
        projectManagementStore.send_emailer_popup_info = {
          title: emailer.title,
          message: emailer.message,
          permissions: emailer.permissions,
          type: emailer.type,
        };
        setEmailerPopupOpen(true);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch emailer details, please try again later",
        );
      });
  };

  const handleSendPreviewEmail = (type: ProjectEmailerType) => {
    projectManagementStore
      .sendPendingEmailerEmail(projectId, type, true)
      .then(() => {
        showSuccess("Preview email sent successfully.");
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch emailer details, please try again later",
        );
      });
  };

  const handleSendEmailer = (type: ProjectEmailerType) => {
    projectManagementStore
      .sendPendingEmailerEmail(projectId, type, false)
      .then(() => {
        showSuccess("Email sent successfully.");
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to fetch emailer details, please try again later",
        );
      });
  };

  const handleHandoverSave = (data: ProjectHandoverData) => {
    projectManagementStore
      .saveHandoverCallInfo(projectId, data)
      .then(() => {
        showSuccess("Handover call details saved successfully.");
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to save handover call details, please try again later.",
        );
      });
  };

  const handleHandoverSendEmail = (
    data: ProjectHandoverData,
    preview_email: boolean,
  ) => {
    projectManagementStore
      .sendHandoverCallInfoEmail(projectId, preview_email)
      .then(() => {
        showSuccess("Handover call details email sent successfully.");
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage ||
            "Failed to send handover call details email, please try again later.",
        );
      });
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                color: theme.palette.customColors.greyTextColor,
              },
              "&:active": {
                color: theme.palette.customColors.greyTextColor,
              },
              mb: 1,
              maxWidth: "60px",
              // pl: { xl: 3, lg: 3, md: 0, sm: 0, xs: 0 },
            }}
            onClick={() => {
              navigate(`/project-management/listing`);
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "16px" }} />
            <span style={{ fontSize: "14px", fontWeight: "600" }}>Back</span>
          </Box>

          <Box
            sx={{
              display: "block",
              "@media (min-width:1285px)": {
                display: "flex",
              },
              justifyContent: "space-between",
              gap: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                "@media (min-width:1200px)": {
                  width: "40%",
                },
              }}
            >
              <CustomerHeading
                brandName={
                  projectManagementStore.currentProjectDetails.customer_name ||
                  ""
                }
                label={orderStore.orderBasicDetails?.label || ""}
                moduleName="Project Details"
                orderId={projectId}
                progressValue={
                  projectManagementStore.currentProjectDetails?.progress
                }
              />
            </Box>

            <Box
              sx={{
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row", md: "row" },
                  gap: 2,
                  alignItems: { xs: "flex-start", sm: "center", md: "center" },
                }}
              >
                {userStore.hasPermission(
                  EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                ) && (
                  <AppButton
                    variant="grayscale"
                    onClick={() => handleDownloadInvoice()}
                    label="Invoice"
                    startIcon={<img src={downloadIcon} alt="downloadIcon" />}
                    customStyles={{ width: { xs: "100%", sm: "auto" } }}
                  />
                )}

                {userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) && (
                  <AppButton
                    variant="grayscale"
                    onClick={() => setSendNpsPopup(!sendNpsPopup)}
                    label="Send NPS"
                    startIcon={<img src={sendNPSIcon} alt="sendNPSIcon" />}
                    customStyles={{ width: { xs: "100%", sm: "auto" } }}
                  />
                )}

                {userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) && (
                  <AppButton
                    variant="grayscale"
                    onClick={() => handleSendCsat()}
                    label="Send CSAT"
                    startIcon={<img src={sendNPSIcon} alt="sendNPSIcon" />}
                    customStyles={{ width: { xs: "100%", sm: "auto" } }}
                  />
                )}

                <AppButton
                  variant="primary"
                  onClick={() =>
                    navigate(`/task-tracker/listing?order_id=${projectId}`)
                  }
                  label="Tasks"
                  startIcon={<></>}
                  customStyles={{
                    width: { xs: "100%", sm: "auto", minWidth: "100px" },
                  }}
                  disabled={
                    projectManagementStore.currentProjectDetails.archived === 1
                  }
                />

                <AppButton
                  variant="secondary"
                  onClick={() =>
                    navigate(
                      `/task-tracker/add-edit-task?order-id=${projectId}`,
                    )
                  }
                  label="Create Task"
                  startIcon={<></>}
                  customStyles={{ width: { xs: "100%", sm: "auto" } }}
                  disabled={
                    projectManagementStore.currentProjectDetails.archived === 1
                  }
                />
              </Box>

              {userStore.hasPermission(EmployeeRoles.CREATE_ORDER) &&
                userStore.hasPermission(EmployeeRoles.EDIT_ORDER) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "center",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Icon
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img src={orderProgressIcon} alt="" />
                      </Icon>
                      <CustomTypography variant="body1">
                        Order status:
                      </CustomTypography>
                    </Box>
                    <Box
                      sx={{
                        maxWidth: "127px",
                        minWidth: "127px",
                      }}
                    >
                      <AppDropDown
                        title=""
                        isMultiSelect={false}
                        fontSize="13px"
                        borderColor={theme.palette.customColors.purple}
                        list={orderStatusOptions}
                        value={String(
                          orderStore.orderBasicDetails?.status || "0",
                        )}
                        onSingleSelectionChange={(value) =>
                          handleStatusUpdate(value, projectId)
                        }
                        borderRadius="10px"
                        height="40px"
                        backgroundColor={theme.palette.customColors.lightPurple}
                        listPadding={"2px"}
                        disabled={
                          projectManagementStore.currentProjectDetails
                            .archived === 1
                        }
                      />
                    </Box>
                    <AppButtonDropdown
                      label={
                        <CustomTypography
                          variant="heading7"
                          sx={{
                            fontWeight: 700,
                            color: theme?.palette?.customColors?.white,
                          }}
                        >
                          Email Actions
                        </CustomTypography>
                      }
                      customStyles={{
                        backgroundColor:
                          theme.palette.customColors.blueGradientStart,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors.blueGradientHoverStart,
                        },
                      }}
                      options={project_emailers.map((emailer) => ({
                        label: emailer.title,
                      }))}
                      onSelect={(option) => {
                        const emailer = project_emailers.find(
                          (emailer) => emailer.title === option,
                        );
                        if (emailer) {
                          if (
                            emailer.type ===
                            ProjectEmailerType.HANDOVER_CALL_DETAILS
                          ) {
                            handleSendHandoverEmail();
                          } else {
                            fetchEmailerInfo(emailer);
                          }
                        }
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                )}
            </Box>
          </Box>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                mb: 0.5,
              }}
              onClick={() => setShowEmailersInfo((prev) => !prev)}
            >
              <CustomTypography variant="heading7">
                Emailers Info
              </CustomTypography>
              <CustomTypography
                variant="body3"
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.customColors.greyTextColor,
                }}
              >
                {showEmailersInfo ? "Hide" : "Show"}
              </CustomTypography>
            </Box>

            {showEmailersInfo && (
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? "#F9FAFB"
                      : "rgba(15,23,42,0.6)",
                  px: { xs: 1, md: 1.5 },
                  py: 1,
                }}
              >
                {projectManagementStore.emailersInfo &&
                projectManagementStore.emailersInfo.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      gap: { xs: 1.5, md: 2 },
                      overflowX: "auto",
                      py: 0.5,
                    }}
                  >
                    {projectManagementStore.emailersInfo
                      .slice()
                      .sort((a, b) => {
                        const aDate =
                          a.email_sent_date ||
                          a.preview_email_sent_date ||
                          a.created_date;
                        const bDate =
                          b.email_sent_date ||
                          b.preview_email_sent_date ||
                          b.created_date;
                        const aTime = aDate ? new Date(aDate).getTime() : 0;
                        const bTime = bDate ? new Date(bDate).getTime() : 0;
                        return aTime - bTime;
                      })
                      .map((info, index, arr) => {
                        const isLast = index === arr.length - 1;
                        const baseDate =
                          info.email_sent_date ||
                          info.preview_email_sent_date ||
                          info.created_date;

                        const statusLabel = info.email_sent
                          ? "Email sent"
                          : info.preview_email_sent
                            ? "Preview sent"
                            : "Not sent";

                        const statusColor = info.email_sent
                          ? theme.palette.success.main
                          : info.preview_email_sent
                            ? theme.palette.warning.main
                            : theme.palette.grey[400];

                        return (
                          <Box
                            key={info.id ?? index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: { xs: 160, md: 200 },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: statusColor,
                                  boxShadow:
                                    "0 0 0 3px rgba(79, 70, 229, 0.15)",
                                  mr: 1,
                                }}
                              />
                              {!isLast && (
                                <Box
                                  sx={{
                                    flex: 1,
                                    height: 2,
                                    minWidth: 20,
                                    background:
                                      "linear-gradient(to right, rgba(148,163,184,0.7), transparent)",
                                  }}
                                />
                              )}
                            </Box>

                            <Box
                              sx={{
                                ml: 1,
                                pr: 0.5,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <CustomTypography
                                variant="body3"
                                fontWeight={600}
                                sx={{ mb: 0.25, fontSize: "0.8rem" }}
                              >
                                {info.type}
                              </CustomTypography>

                              <CustomTypography
                                variant="body3"
                                color={theme.palette.customColors.greyTextColor}
                                sx={{ mb: 0.15, fontSize: "0.7rem" }}
                              >
                                {statusLabel}
                                {info.emails_count
                                  ? ` • ${info.emails_count} sent`
                                  : ""}
                              </CustomTypography>

                              {baseDate && (
                                <CustomTypography
                                  variant="body3"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  {formatDate(baseDate, true, false)}
                                </CustomTypography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                ) : (
                  <CustomTypography
                    variant="body3"
                    color={theme.palette.customColors.greyTextColor}
                  >
                    No emailers have been sent for this project yet.
                  </CustomTypography>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ mt: 2, mb: 2 }} />
          <Box>
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
            >
              Project Approved By:{" "}
              <span
                style={{
                  fontWeight: 600,
                  color: theme?.palette?.customColors?.blackBorder,
                }}
              >
                {projectManagementStore.currentProjectDetails.creator_name ||
                  "--"}
              </span>{" "}
              {"on"}{" "}
              <span
                style={{
                  fontWeight: 600,
                  color: theme?.palette?.customColors?.blackBorder,
                }}
              >
                {formatDate(
                  projectManagementStore.currentProjectDetails.created_date ||
                    "",
                )}
              </span>
            </CustomTypography>
          </Box>
          <Box
            sx={{
              display: { md: "block", lg: "flex" },
              justifyContent: { lg: "space-between", md: "none" },
              gap: { xl: 12, lg: 10 },
              flexWrap: "wrap",
            }}
            mt={4}
          >
            {/* Left Side*/}
            <Box sx={{ flex: 0.4, minWidth: "60%" }}>
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 20 }}
              >
                Project Management
              </CustomTypography>
              <Grid2 container spacing={5}>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      PM POC
                    </CustomTypography>
                    <AppSingleSearchDropdown
                      key={"admin-pm"}
                      fontSize="14px"
                      showBorder={true}
                      inputHeight="40px"
                      list={
                        TaskTrackerStore?.admins?.map((admin: Admin) => ({
                          id: admin.id.toString(),
                          value: admin.name,
                        })) || []
                      }
                      value={
                        projectManagementStore.currentProjectDetails.pm_admin_id
                          ? {
                              id: String(
                                projectManagementStore.currentProjectDetails
                                  .pm_admin_id,
                              ),
                              value:
                                projectManagementStore.currentProjectDetails
                                  .pm_admin_name!,
                            }
                          : null
                      }
                      handleSearch={handleAdminSearch}
                      handleSelect={(selectedItem: FilterValue | null) => {
                        projectManagementStore.updateCurrentProjectDetails(
                          "pm_admin_id",
                          selectedItem ? Number(selectedItem.id) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "pm_admin_name",
                          selectedItem?.value || undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Sales POC
                    </CustomTypography>
                    <AppSingleSearchDropdown
                      key={"sales-pm"}
                      fontSize="14px"
                      showBorder={true}
                      inputHeight="40px"
                      list={
                        TaskTrackerStore?.admins?.map((admin: Admin) => ({
                          id: admin.id.toString(),
                          value: admin.name,
                        })) || []
                      }
                      value={
                        projectManagementStore.currentProjectDetails
                          .sales_admin_id
                          ? {
                              id: String(
                                projectManagementStore.currentProjectDetails
                                  .sales_admin_id,
                              ),
                              value:
                                projectManagementStore.currentProjectDetails
                                  .sales_admin_name!,
                            }
                          : null
                      }
                      handleSearch={handleAdminSearch}
                      handleSelect={(selectedItem: FilterValue | null) => {
                        projectManagementStore.updateCurrentProjectDetails(
                          "sales_admin_id",
                          selectedItem ? Number(selectedItem.id) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "sales_admin_name",
                          selectedItem?.value || undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Payment Status
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={ProjectPaymentStatus || []}
                      value={
                        projectManagementStore.currentProjectDetails &&
                        projectManagementStore.currentProjectDetails
                          .payment_status_id
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .payment_status_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_status_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_status_label",
                          selected
                            ? ProjectPaymentStatus.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Type Of Order
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={OrderType || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.order_type_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .order_type_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "order_type_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "order_type_label",
                          selected
                            ? OrderType.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Warehouse
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={warehouseStore.warehouses.map((warehouse) => ({
                        id: warehouse.id,
                        value: warehouse.identifier,
                      }))}
                      value={
                        projectManagementStore.currentProjectDetails
                          .warehouse_id
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(id: string, name: string) => {
                        projectManagementStore.updateCurrentProjectDetails(
                          "warehouse_id",
                          id,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "warehouse_identifier",
                          name,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 4 }} />
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 20 }}
              >
                Delivery & Shipment
              </CustomTypography>
              <Grid2 container spacing={5}>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Dispatch Date
                    </CustomTypography>

                    <DatePicker2
                      label=""
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.dispatch_date
                          ? dayjs(
                              projectManagementStore.currentProjectDetails
                                .dispatch_date,
                            )
                          : null
                      }
                      onChange={(date: Dayjs | null) => {
                        if (!date) return;

                        const formattedDate = date?.format(
                          "YYYY-MM-DD HH:mm:ss",
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "dispatch_date",
                          formattedDate,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Delivery Date
                    </CustomTypography>
                    <DatePicker2
                      label=""
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.delivery_date
                          ? dayjs(
                              projectManagementStore.currentProjectDetails
                                .delivery_date,
                            )
                          : null
                      }
                      onChange={(date: Dayjs | null) => {
                        if (!date) return;

                        const formattedDate = date?.format(
                          "YYYY-MM-DD HH:mm:ss",
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "delivery_date",
                          formattedDate,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Mode of Shipment
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={ShipmentModes || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.shipment_mode_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .shipment_mode_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipment_mode_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipment_mode_label",
                          selected
                            ? ShipmentModes.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Type Of Shipment
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={ShippingType || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.shipping_type_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .shipping_type_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipping_type_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "shipping_type_label",
                          selected
                            ? ShippingType.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Flexible Delivery
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={[
                        { id: "-1", value: "" },
                        { id: "0", value: "No" },
                        { id: "1", value: "Yes" },
                      ]}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.is_delivery_date_flexible === undefined
                          ? "-1"
                          : projectManagementStore.currentProjectDetails
                                .is_delivery_date_flexible
                            ? "1"
                            : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      disabled={true}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                  <Box sx={{ width: "100%" }}>
                    <CustomTypography
                      variant="body3"
                      style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        textTransform: "uppercase",
                        letterSpacing: "0.02em",
                        opacity: 0.85,
                      }}
                    >
                      Mode Of Payment
                    </CustomTypography>
                    <AppDropDown
                      title=""
                      list={PaymentMode || []}
                      value={
                        projectManagementStore.currentProjectDetails
                          ?.payment_mode_id !== undefined
                          ? String(
                              projectManagementStore.currentProjectDetails
                                .payment_mode_id,
                            )
                          : "0"
                      }
                      isMultiSelect={false}
                      fontSize={"13px"}
                      borderColor={theme.palette.customColors.greyDarkborder}
                      onSingleSelectionChange={(value) => {
                        const selected = value ?? null;
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_mode_id",
                          selected ? Number(value) : undefined,
                        );
                        projectManagementStore.updateCurrentProjectDetails(
                          "payment_mode_label",
                          selected
                            ? PaymentMode.find(
                                (status) => Number(status.id) === Number(value),
                              )?.value
                            : undefined,
                        );
                      }}
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 4 }} />
              <CustomTypography
                variant="heading5"
                style={{ fontWeight: 600, marginBottom: 20 }}
              >
                Project Notes
              </CustomTypography>
              <Grid2 container spacing={5}>
                <Grid2 size={{ xs: 12 }}>
                  <Box>
                    <RichTextEditor
                      value={
                        projectManagementStore.currentProjectDetails
                          .order_additional_info || ""
                      }
                      onChange={(value: string) =>
                        projectManagementStore.updateProjectNotes(value)
                      }
                      maxLength={1000}
                      placeholder={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                          ? ""
                          : "You can add any additional information here. Eg. Product wise initial information, packing instructions, link to the drive with logos and creatives, etc."
                      }
                      disabled={
                        projectManagementStore.currentProjectDetails
                          .archived === 1
                      }
                    />
                  </Box>
                </Grid2>
              </Grid2>

              {orderStore.orderBasicDetails?.services &&
                orderStore.orderBasicDetails?.services?.length > 0 && (
                  <>
                    <Divider sx={{ my: 4 }} />
                    <CustomTypography
                      variant="heading5"
                      style={{ fontWeight: 600, marginBottom: 20 }}
                    >
                      Added Services
                    </CustomTypography>
                    <Grid2 container spacing={5}>
                      <Grid2 size={{ xs: 12 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            mt: 2,
                            border: `1px solid ${theme.palette.customColors.greyDarkborder}`,
                            borderRadius: "4px",
                            padding: 2,
                          }}
                        >
                          {orderStore.orderBasicDetails?.services.map(
                            (service) => {
                              const icon = service.vector_image_url;
                              return (
                                <Chip
                                  key={service.id}
                                  label={
                                    service.name + " " + service.custom_name
                                  }
                                  avatar={
                                    icon ? (
                                      <Icon style={{ fontSize: "15px" }}>
                                        <img
                                          src={icon}
                                          alt={service.custom_name || ""}
                                        />
                                      </Icon>
                                    ) : undefined
                                  }
                                  sx={{
                                    backgroundColor:
                                      theme.palette.customColors.greyBackground,
                                    fontWeight: 600,
                                    borderRadius: "4px",
                                    height: "32px",
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                  }}
                                />
                              );
                            },
                          )}
                        </Box>
                      </Grid2>
                    </Grid2>
                  </>
                )}

              <Grid2 container spacing={5} sx={{ mt: 2 }}>
                <Grid2 size={{ xs: 12 }}>
                  {userStore.hasPermission(EmployeeRoles.CREATE_PROJECT) &&
                    userStore.hasPermission(EmployeeRoles.EDIT_PROJECT) && (
                      <AppButton
                        variant="primary"
                        label="Save"
                        customStyles={{
                          width: "120px",
                        }}
                        onClick={() => handleSave()}
                        disabled={
                          projectManagementStore.currentProjectDetails
                            .archived === 1
                        }
                      />
                    )}
                </Grid2>
              </Grid2>
            </Box>

            {/* Right Side*/}
            <Box
              mt={{ sm: 2, xs: 2, mt: 2 }}
              sx={{
                flex: 0.3,
                minWidth: "30%",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <CustomerDetailsCard />
              <Card
                sx={{
                  padding: { xl: 4, lg: 3, md: 3, sm: 2, xs: 2 },
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: `1px solid ${theme.palette.customColors.greyBorder}`,
                }}
              >
                <Grid2 size={{ xs: 12 }}>
                  <CustomTypography
                    variant="heading5"
                    style={{ fontWeight: 600, marginBottom: 20 }}
                  >
                    Exclude From Analytics
                  </CustomTypography>
                  <Box sx={{ margin: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={
                            Boolean(
                              projectManagementStore.currentProjectDetails
                                .excluded_from_projects,
                            ) === true
                          }
                          onChange={(_, checked) => {
                            if (checked) setExcludeFromProjectPopup(checked);
                            else
                              projectManagementStore.updateExcludeFromProjects(
                                false,
                                null,
                              );
                          }}
                          disabled={
                            projectManagementStore.currentProjectDetails
                              .archived === 1
                          }
                        />
                      }
                      label={
                        <CustomTypography
                          variant="heading8"
                          color={theme.palette.customColors.blackBorder}
                        >
                          Projects
                        </CustomTypography>
                      }
                      sx={{ fontSize: "0.5rem", marginBottom: 0.8 }}
                    />
                    {Boolean(
                      projectManagementStore.currentProjectDetails
                        .excluded_from_projects,
                    ) &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_projects &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_projects.length > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color={theme.palette.customColors.greyTextColor}
                            style={{ marginBottom: 0.8 }}
                          >
                            Reason:{" "}
                            {
                              projectManagementStore.currentProjectDetails
                                .reason_to_exclude_from_projects
                            }
                          </CustomTypography>
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_projects_by_name && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                              style={{ marginBottom: 0.8 }}
                            >
                              Admin:{" "}
                              {
                                projectManagementStore.currentProjectDetails
                                  .excluded_from_projects_by_name
                              }
                            </CustomTypography>
                          )}
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_project_date && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                            >
                              Date:{" "}
                              {projectManagementStore.currentProjectDetails
                                .excluded_from_project_date
                                ? new Date(
                                    projectManagementStore.currentProjectDetails
                                      .excluded_from_project_date,
                                  ).toLocaleString()
                                : ""}
                            </CustomTypography>
                          )}
                        </Box>
                      )}
                  </Box>
                  <Box sx={{ margin: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={
                            Boolean(
                              projectManagementStore.currentProjectDetails
                                .excluded_from_sourcing,
                            ) === true
                          }
                          onChange={(_, checked) => {
                            if (checked) setExcludeFromSourcingPopup(checked);
                            else
                              projectManagementStore.updateExcludeFromSourcing(
                                false,
                                null,
                              );
                          }}
                          disabled={
                            projectManagementStore.currentProjectDetails
                              .archived === 1
                          }
                        />
                      }
                      label={
                        <CustomTypography
                          variant="heading7"
                          color={theme.palette.customColors.blackBorder}
                        >
                          Sourcing
                        </CustomTypography>
                      }
                      sx={{ fontSize: "0.5rem", marginBottom: 0.8 }}
                    />
                    {Boolean(
                      projectManagementStore.currentProjectDetails
                        .excluded_from_sourcing,
                    ) &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_sourcing &&
                      projectManagementStore.currentProjectDetails
                        .reason_to_exclude_from_sourcing.length > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color={theme.palette.customColors.greyTextColor}
                            style={{ marginBottom: 0.8 }}
                          >
                            Reason:{" "}
                            {
                              projectManagementStore.currentProjectDetails
                                .reason_to_exclude_from_sourcing
                            }
                          </CustomTypography>
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_sourcing_by_name && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                              style={{ marginBottom: 0.8 }}
                            >
                              Admin:{" "}
                              {
                                projectManagementStore.currentProjectDetails
                                  .excluded_from_sourcing_by_name
                              }
                            </CustomTypography>
                          )}
                          {projectManagementStore.currentProjectDetails
                            .excluded_from_sourcing_date && (
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                            >
                              Date:{" "}
                              {projectManagementStore.currentProjectDetails
                                .excluded_from_sourcing_date
                                ? new Date(
                                    projectManagementStore.currentProjectDetails
                                      .excluded_from_sourcing_date,
                                  ).toLocaleString()
                                : ""}
                            </CustomTypography>
                          )}
                        </Box>
                      )}
                  </Box>
                </Grid2>
              </Card>
            </Box>
          </Box>
          <Box>
            <SendNpsPopup
              open={sendNpsPopup}
              onClose={() => setSendNpsPopup(!sendNpsPopup)}
              onSubmit={(name: string, email: string) => {
                handleSendNps(name, email);
                setSendNpsPopup(!sendNpsPopup);
              }}
            />
          </Box>
          <ConfirmationPopup
            id={projectManagementStore.currentProjectDetails.order_id}
            title={"Reason"}
            content={`You are going to exclude project ${projectManagementStore.currentProjectDetails.order_id} from projects analytics. State your reason here?`}
            type="form"
            inputRequired={true}
            inputLabel="Reason"
            inputType="text"
            onCancel={() => {
              setExcludeFromProjectPopup(false);
              projectManagementStore.updateExcludeFromProjects(false, null);
            }}
            onConfirm={(id, input) => {
              if (input) {
                setExcludeFromProjectPopup(false);
                projectManagementStore.updateExcludeFromProjects(true, input);
              }
            }}
            open={excludeFromProjectPopup}
          />
          <ConfirmationPopup
            id={projectManagementStore.currentProjectDetails.order_id}
            title={"Reason"}
            content={`You are going to exclude project ${projectManagementStore.currentProjectDetails.order_id} from sourcing analytics. State your reason here?`}
            type="form"
            inputRequired={true}
            inputLabel="Reason"
            inputType="text"
            onCancel={() => {
              projectManagementStore.updateExcludeFromSourcing(false, null);
              setExcludeFromSourcingPopup(false);
            }}
            onConfirm={(id, input) => {
              if (input) {
                setExcludeFromSourcingPopup(false);
                projectManagementStore.updateExcludeFromSourcing(true, input);
              }
            }}
            open={excludeFromSourcingPopup}
          />

          <HandoverCallDetailsPopup
            open={handoverPopupOpen}
            data={projectManagementStore.handoverCallInfo}
            onClose={() => setHandoverPopupOpen(false)}
            onSave={handleHandoverSave}
            sendEmail={handleHandoverSendEmail}
          />

          {projectManagementStore.send_emailer_popup_info && (
            <SendEmailerConfirmationPopup
              open={emailerPopupOpen}
              title={projectManagementStore.send_emailer_popup_info.title}
              message={projectManagementStore.send_emailer_popup_info.message}
              data={projectManagementStore.emailerInfo}
              type={projectManagementStore.send_emailer_popup_info.type}
              onClose={() => {
                setEmailerPopupOpen(false);
                projectManagementStore.send_emailer_popup_info = null;
              }}
              onSendPreviewEmail={handleSendPreviewEmail}
              onSendEmail={handleSendEmailer}
              can_send_email={userStore.hasPermission(
                projectManagementStore.send_emailer_popup_info.permissions,
                true,
              )}
              disabled={false}
            />
          )}
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </Grid2>
  );
};

export default observer(ProjectDetails);
